import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLinkIcon } from 'LoggedOut/ExternalLinkIcon';
import { getChannel } from 'utils';
import {
  BookingStage,
  FieldValues,
  FormValues,
  FormikStatus,
} from '../../types';
import {
  HelpLinkContainer,
  HelpText,
  Anchor,
  ConfirmButton,
  ConfirmContainer,
  ErrorText,
  ErrorContainer,
  ErrorIcon,
} from './styles';

interface ConfirmFooterProps {
  formValues: FormValues;
  setFieldValue(field: FieldValues, value: unknown): void;
  isSubmitting: boolean;
  isValid: boolean;
  isDirectBookingEnabled?: boolean;
  status?: FormikStatus;
}

export default ({
  setFieldValue,
  formValues,
  isSubmitting,
  isValid,
  isDirectBookingEnabled = false,
  status,
}: ConfirmFooterProps) => {
  const { t } = useTranslation('book', { keyPrefix: 'practitioner' });
  const { supportUrl } = getChannel();
  const bookingStage = formValues[FieldValues.Stage];

  const buttonType =
    formValues[FieldValues.Stage] === BookingStage.DateSelect
      ? 'button'
      : 'submit';

  const onClick = useCallback(() => {
    if (bookingStage === BookingStage.DateSelect) {
      setFieldValue(FieldValues.Stage, BookingStage.Confirm);
    }
  }, [bookingStage, setFieldValue]);

  return (
    <ConfirmContainer>
      {status?.submissionError?.error && (
        <ErrorContainer>
          <ErrorIcon />
          <ErrorText>{status.submissionError.message}</ErrorText>
        </ErrorContainer>
      )}

      <HelpLinkContainer>
        <HelpText>{t('help.title')}</HelpText>{' '}
        <Anchor forwardedAs="a" href={supportUrl} target="_blank">
          {t('help.text')}
          <ExternalLinkIcon />
        </Anchor>
      </HelpLinkContainer>

      <ConfirmButton
        variant="primary"
        key={formValues[FieldValues.Stage]}
        $success={!!status?.submissionComplete}
        loading={isSubmitting}
        disabled={isSubmitting || !isValid || status?.submissionComplete}
        label={
          status?.submissionComplete
            ? t(
                `book.ctas.${
                  isDirectBookingEnabled ? 'confirmed' : 'requestSent'
                }`,
              )
            : t('book.ctas.confirm')
        }
        type={buttonType}
        onClick={onClick}
        data-cy="practitioner-booking-confirm-button"
      />
    </ConfirmContainer>
  );
};
